import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../../redux/store";
import { SurveyQuestion } from "../../../types";
import { updateQuestion } from "../../../../../../redux/surveySlice";
import { useEffect, useRef, useState } from "react";
import "./SurveyQuestionsListDroppable.css";
import { createPortal } from "react-dom";

interface SurveyQuestionsListDroppableProps {
    setIndex: (index: number) => void;
    handleDelete: (question: SurveyQuestion, index: number) => void;
    handleCopy: (question: SurveyQuestion, index: number) => void;
}

export default function SurveyQuestionsListDroppable({ setIndex, handleDelete, handleCopy}: SurveyQuestionsListDroppableProps) {
    const dispatch = useDispatch();

    const questions = useSelector((state: RootState) => state.surveyForm.data.questions ?? []);
    const errors = useSelector((state: RootState) => state.surveyForm.errors);
    const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const menuRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setOpenMenuIndex(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const onDragEnd = (result: any) => {
        if (!result.destination) return;
        const items = [...questions].sort((a, b) => a.order - b.order);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        items.map((quotation, order) => dispatch(updateQuestion({ index: quotation.index, field: 'order', value: order + 1 })))
    };

    const handleTextClick = (e: React.MouseEvent, questionIndex: number) => {
        e.stopPropagation();
        setIndex(questionIndex);
    };

    const handleDeleteClick = (e: React.MouseEvent, question: SurveyQuestion, index: number) => {
        e.stopPropagation();
        handleDelete(question, index);
    };

    const handleOpenOptionsClick = (e: React.MouseEvent, index: number) => {
        e.stopPropagation();
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setMenuPosition({ top: rect.bottom, left: rect.left});
        setOpenMenuIndex(openMenuIndex === index ? null : index);
    };

    const handleCopyClick = (e: React.MouseEvent, question: SurveyQuestion, index: number) => {
        e.stopPropagation();
        handleCopy(question, index);
    };

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="questions-list">
                {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                        {[...questions]
                            .sort((a, b) => a.order - b.order)
                            .map((question, index) => {
                                const hasErrors = Object.keys(errors).some(key =>
                                    key.startsWith(`questions[${question.index}]`)
                                );
                                const draggableKey = question.id
                                    ? `id-${question.id}`
                                    : `index-${question.index}-${index}`;
                                return (
                                    <Draggable
                                        key={draggableKey}
                                        draggableId={draggableKey}
                                        index={index}
                                    >
                                        {(provided, snapshot) => (
                                            <Row
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className={`question-list-item-container ${hasErrors ? "has-errors" : ""} ${snapshot.isDragging ? "dragging" : ""}`}
                                                style={provided.draggableProps.style}
                                            >
                                                <Col
                                                    md={10}
                                                    className="question-text-col"
                                                    onClick={(e) => handleTextClick(e, question.index)}
                                                >
                                                    {index + 1}. {question.text?.catalan}
                                                </Col>
                                                <Col md={2}>
                                                    <i
                                                        className="bi bi-three-dots-vertical question-options-icon"
                                                        onClick={(e) => handleOpenOptionsClick(e, question.index)}
                                                    ></i>
                                                    {openMenuIndex === question.index && createPortal(
                                                        <div
                                                            ref={menuRef}
                                                            className="question-options-menu"
                                                            style={{ 
                                                                position: 'fixed',
                                                                top: menuPosition.top, 
                                                                left: menuPosition.left,
                                                                zIndex: 9999
                                                            }}
                                                        >
                                                            <div
                                                                className="question-options-menu-item question-options-menu-item--delete"
                                                                onClick={(e) => {
                                                                    setOpenMenuIndex(null);
                                                                    handleDeleteClick(e, question, question.index);
                                                                }}
                                                            >
                                                                <i className="bi bi-trash3" />
                                                                <span>Delete</span>
                                                            </div>
                                                            <div
                                                                className="question-options-menu-item"
                                                                onClick={(e) => {
                                                                    setOpenMenuIndex(null);
                                                                    handleCopyClick(e, question, question.index);
                                                                }}
                                                            >
                                                                <i className="bi bi-copy" />
                                                                <span>Copy</span>
                                                            </div>
                                                        </div>,
                                                        document.body
                                                    )}
                                                </Col>
                                            </Row>
                                        )}
                                    </Draggable>
                                );
                            })}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
}
